import { Stack, Text, tokens } from '@hotjar/hotsauce';
import { useTranslationWithPrefix } from '@hotjar/translations';
import styled from '@emotion/styled';
import type { CountryCode } from '@hotjar/country-lib';
import {
  useExperimentChurnDeflectionModalV1,
  showChurnDeflectionModal as showChurnDeflectionModalV1,
  showChurnSurveyModal,
} from '@hotjar/voluntary-churn';
import { ConfirmOrderContent } from '../ConfirmOrderContent';
import { TextWithLineBreak, Image } from '../../ConfirmOrderContainer.styles';
import processErrorGraphic from '../../../../../resources/images/processed-error.svg';
import { showChurnDeflectionModal } from '~/subscription-management/features/churn/churn-deflection-modal/ChurnDeflectionModal';
import type { Context } from '~/subscription-management/checkout/state-machine/checkout.machine.types';
import { prettifyDate } from '~/subscription-management/utils/date.utils';
import type { Customer, Currency } from '~/subscription-management/api/common/model/customer';
import type {
  ProductAllowance,
  BillingPeriod,
} from '~/subscription-management/api/common/model/subscription';

interface Props {
  onSubmit: () => void;
  onCancel: () => void;
  allowancesToRemove: ProductAllowance[];
  accountId: Context['accountId'];
  currentPeriodEndDate: string | undefined;
  currentPeriodCostSummary: Customer['currentPeriodCostSummary'] | undefined;
  country: CountryCode | undefined;
  billingPeriod: BillingPeriod;
  currency: Currency;
}

const S = {
  Stack: styled(Stack)`
    align-items: center;
    text-align: center;
  `,
  Image: styled(Image)`
    width: 38%;
  `,
  TextWithLineBreak: styled(TextWithLineBreak)`
    margin-top: ${tokens.spacer.x2};
  `,
};

export function Overdue({
  onSubmit,
  onCancel,
  allowancesToRemove,
  accountId,
  currentPeriodEndDate,
  currentPeriodCostSummary,
  country,
  billingPeriod,
  currency,
}: Props): JSX.Element {
  const { t } = useTranslationWithPrefix('checkout:overdue');
  const isChurnDeflectionModalV1ExperimentEnabled = useExperimentChurnDeflectionModalV1(
    allowancesToRemove,
    {
      billingPeriod,
      currency,
    },
  );

  const nextChargeDate = prettifyDate(currentPeriodEndDate, country);

  /**
   * this is a duplicate of the churn flow that happens inside ChurnFooter for the sake of speed
   * TODO: there is a dedicated slot for the churn flow inside the checkout machine... Move it there and just transition to it
   */
  const handleChurn = async () => {
    try {
      if (isChurnDeflectionModalV1ExperimentEnabled) {
        await showChurnDeflectionModalV1({
          subscriptionEndDate: nextChargeDate,
          currentProducts: allowancesToRemove,
          billingPeriod,
          currency,
        });
      } else {
        await showChurnDeflectionModal({
          subscriptionEndDate: nextChargeDate,
          currentProducts: allowancesToRemove,
        });
      }
      await showChurnSurveyModal({
        subscriptionSubtotal: currentPeriodCostSummary?.amountWithoutTax.toString() ?? '',
        accountId: accountId.toString(),
        allowancesToRemove,
      });
      onCancel();
    } catch {
      /** no-op */
    }
  };

  return (
    <ConfirmOrderContent
      contentLabel={t('title')}
      onCancel={() => handleChurn()}
      onSubmit={() => onSubmit()}
      footerActions={[
        {
          kind: 'ghost',
          children: t('action.cancel'),
          onClickType: 'cancel',
        },
        {
          kind: 'primary',
          children: t('action.submit'),
          onClickType: 'submit',
        },
      ]}
    >
      <S.Stack spacing="x3" direction="column">
        <S.Image src={processErrorGraphic} alt={t('title')} />

        <Text textType="header4" as="h1">
          {t('title')}
        </Text>

        <TextWithLineBreak>{t('description')}</TextWithLineBreak>
      </S.Stack>
    </ConfirmOrderContent>
  );
}
